import React, { useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FiX, FiChevronDown, FiChevronRight, FiDownload } from "react-icons/fi";
import { Loader2 } from "lucide-react";
import taskImportService from "../services/taskImportService";
import sprintService from "../services/sprintService";
import { pickFromGoogleDrive } from "../lib/googlePicker";
import { downloadAutoTaskTemplate } from "../lib/autoTaskTemplate";

/**
 * ─────────────────────────────────────────────
 * Auto Task
 * ─────────────────────────────────────────────
 * Builds Kanban lists and their tasks from an uploaded breakdown.
 *
 * Deliberately a two-step flow: read the file, then review what was found.
 * Nothing reaches the board until Import is pressed, because a misread task
 * is far cheaper to drop here than to delete from a live board afterwards.
 */

const TYPE_STYLES = {
  requirements: "bg-[#EEF2FF] text-[#4338CA]",
  design: "bg-[#FDF2F8] text-[#BE185D]",
  frontend: "bg-[#ECFEFF] text-[#0E7490]",
  backend: "bg-[#F0FDF4] text-[#15803D]",
  testing: "bg-[#FEF3C7] text-[#92400E]",
  deployment: "bg-[#F5F3FF] text-[#6D28D9]",
  others: "bg-[#F1F5F9] text-[#475569]",
};

const PRIORITY_STYLES = {
  low: "bg-[#F1F5F9] text-[#475569]",
  medium: "bg-[#DBEAFE] text-[#1D4ED8]",
  high: "bg-[#FFEDD5] text-[#C2410C]",
  critical: "bg-[#FEE2E2] text-[#B91C1C]",
};

const STATUS_LABELS = {
  todo: "To do",
  "in-progress": "In progress",
  done: "Done",
};

const AutoTask = () => {
  const navigate = useNavigate();
  const { projectRef } = useParams();
  const projectId = projectRef?.split("-").pop();

  const [file, setFile] = useState(null);
  const [driveFile, setDriveFile] = useState(null);
  const [lists, setLists] = useState([]);
  const [collapsed, setCollapsed] = useState({});
  const [isParsing, setIsParsing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [isOpeningDrive, setIsOpeningDrive] = useState(false);
  const [error, setError] = useState("");
  const [driveConfig, setDriveConfig] = useState({ available: false });
  const fileInputRef = useRef(null);

  // Whether Google Drive is configured. Asked once, from the endpoint that
  // already reports it, so no new backend surface is needed.
  React.useEffect(() => {
    if (!projectId) return;
    sprintService
      .getAvailableLists(projectId)
      .then((r) => setDriveConfig(r.googleDrive || { available: false }))
      .catch(() => setDriveConfig({ available: false }));
  }, [projectId]);

  const backToBoard = () => navigate(`/dashboard/kanban/${projectRef}`);

  const chooseFromDrive = async () => {
    setError("");
    setIsOpeningDrive(true);
    try {
      const picked = await pickFromGoogleDrive(driveConfig);
      if (picked) {
        setDriveFile(picked);
        setFile(null);
      }
    } catch (err) {
      setError(err.message || "Could not open Google Drive.");
    } finally {
      setIsOpeningDrive(false);
    }
  };

  const readFile = async () => {
    if ((!file && !driveFile) || !projectId) return;
    setIsParsing(true);
    setError("");
    try {
      const response = driveFile
        ? await taskImportService.parseDriveFile(projectId, driveFile)
        : await taskImportService.parseFile(projectId, file);
      // Everything starts included; the user unticks what they do not want.
      setLists(
        (response.data || []).map((list) => ({
          ...list,
          include: true,
          tasks: list.tasks.map((task) => ({ ...task, include: true })),
        })),
      );
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || "Could not read that file.",
      );
    } finally {
      setIsParsing(false);
    }
  };

  const toggleList = (li) =>
    setLists((prev) =>
      prev.map((list, i) =>
        i === li
          ? {
              ...list,
              include: !list.include,
              // Unticking a list unticks everything under it, so the count
              // at the bottom always matches what will actually be created.
              tasks: list.tasks.map((t) => ({ ...t, include: !list.include })),
            }
          : list,
      ),
    );

  const toggleTask = (li, ti) =>
    setLists((prev) =>
      prev.map((list, i) => {
        if (i !== li) return list;
        const tasks = list.tasks.map((t, j) =>
          j === ti ? { ...t, include: !t.include } : t,
        );
        // A list with nothing ticked is itself untickled, and ticking any
        // task brings its list back.
        return { ...list, tasks, include: tasks.some((t) => t.include) };
      }),
    );

  const editTask = (li, ti, field, value) =>
    setLists((prev) =>
      prev.map((list, i) =>
        i === li
          ? {
              ...list,
              tasks: list.tasks.map((t, j) =>
                j === ti ? { ...t, [field]: value } : t,
              ),
            }
          : list,
      ),
    );

  const editListName = (li, value) =>
    setLists((prev) =>
      prev.map((list, i) => (i === li ? { ...list, name: value } : list)),
    );

  const chosen = lists
    .filter((l) => l.include && l.name.trim())
    .map((l) => ({
      name: l.name.trim(),
      tasks: l.tasks
        .filter((t) => t.include && t.name.trim())
        // Drop the UI-only flags before sending.
        .map(({ include, ...task }) => task),
    }))
    .filter((l) => l.tasks.length > 0);

  const totalChosen = chosen.reduce((n, l) => n + l.tasks.length, 0);

  const doImport = async () => {
    if (!totalChosen || !projectId) return;
    setIsImporting(true);
    setError("");
    try {
      await taskImportService.importBoard(projectId, chosen);
      backToBoard();
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Could not import those tasks.",
      );
      setIsImporting(false);
    }
  };

  const startOver = () => {
    setLists([]);
    setFile(null);
    setDriveFile(null);
    setError("");
  };

  return (
    <div className="p-4 sm:p-6 font-[Poppins]">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-[24px] font-semibold text-[#151A2D]">Auto Task</h1>
          <p className="mt-1 text-[14px] text-[#667085]">
            {lists.length
              ? "Check what was found, then import it onto the board."
              : "Upload a task breakdown and the lists and tasks are read for you."}
          </p>
        </div>
        <div className="flex shrink-0 gap-3">
          <button
            type="button"
            onClick={downloadAutoTaskTemplate}
            title="Download a blank Excel template to fill in"
            className="flex h-10 shrink-0 items-center gap-2 rounded-lg border border-[#DCE2F0] px-4 text-[14px] text-[#4253C7] hover:bg-[#F5F6FF]"
          >
            <FiDownload size={16} />
            Template
          </button>
          <button
            type="button"
            onClick={backToBoard}
            className="flex h-10 shrink-0 items-center gap-2 rounded-lg border border-[#DCE2F0] px-4 text-[14px] text-[#475569] hover:bg-[#F7F8FC]"
          >
            <FiX size={16} />
            Back to board
          </button>
        </div>
      </div>

      {error && (
        <p className="mt-4 rounded-lg bg-[#FEF3F2] px-4 py-3 text-[13px] text-[#B42318]">
          {error}
        </p>
      )}

      {lists.length === 0 ? (
        /* ── Step 1: choose a file ─────────────────────────────── */
        <div className="mt-6 rounded-[14px] bg-white p-6 shadow-[0_0_4px_rgba(0,0,0,0.06)]">
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xlsm,.csv,.pdf,.txt,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv,text/plain"
            className="hidden"
            onChange={(e) => {
              setFile(e.target.files?.[0] || null);
              setDriveFile(null);
              setError("");
              e.target.value = "";
            }}
          />
          <div className={`grid gap-4 ${driveConfig.available ? "sm:grid-cols-2" : "grid-cols-1"}`}>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex min-h-[170px] flex-col items-center justify-center rounded-lg border border-dashed border-[#B8C0CC] bg-[#FBFCFF] px-6 text-center hover:bg-[#F5F6FF]"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mb-3 h-7 w-7 text-[#4253C7]" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>
              <span className="text-[15px] font-semibold text-[#4253C7]">Upload from this device</span>
              <span className="mt-1 text-[12px] text-[#7D89A3]">Excel (.xlsx), CSV or PDF · up to 10 MB</span>
            </button>
            {driveConfig.available && (
              <button
                type="button"
                onClick={chooseFromDrive}
                disabled={isOpeningDrive}
                className="flex min-h-[170px] flex-col items-center justify-center rounded-lg border border-dashed border-[#B8C0CC] bg-[#FBFCFF] px-6 text-center hover:bg-[#F5F6FF] disabled:cursor-not-allowed disabled:opacity-60"
              >
                <svg viewBox="0 0 24 24" aria-hidden="true" className="mb-3 h-7 w-7"><path fill="#0066DA" d="M3.4 17.2 1.8 14.4c-.2-.3-.2-.7 0-1l5-8.7 3.2 5.6-5 8.7c-.3.5-1.1.6-1.6.2z" /><path fill="#00AC47" d="M12 3.1h5.8c.4 0 .7.2.9.5l5 8.7c.2.3.2.7 0 1l-1.6 2.8-8.9-.1L12 3.1z" /><path fill="#EA4335" d="M4.9 20.9c-.5 0-.9-.3-1.1-.7L3.4 17.2h17.3l-1.6 2.8c-.2.4-.6.6-1 .6H4.9z" /></svg>
                <span className="text-[15px] font-semibold text-[#4253C7]">{isOpeningDrive ? "Opening Drive…" : "Upload from Google Drive"}</span>
                <span className="mt-1 text-[12px] text-[#7D89A3]">Sheets, Docs, PDFs and uploaded files</span>
              </button>
            )}
          </div>

          {(file || driveFile) && (
            <p className="mt-4 truncate text-[13px] text-[#465675]" title={driveFile?.name || file?.name}>
              Selected: {driveFile?.name || file?.name}
              {driveFile && <span className="ml-1.5 text-[12px] text-[#7D89A3]">(Google Drive)</span>}
            </p>
          )}

          <p className="mt-4 text-[12px] leading-[18px] text-[#7D89A3]">
            The file is read for its task table and then discarded — it is not stored.
            Nothing reaches the board until you press Import.
          </p>

          <div className="mt-5 flex justify-end">
            <button
              type="button"
              onClick={readFile}
              disabled={(!file && !driveFile) || isParsing}
              className="flex h-10 min-w-[130px] items-center justify-center gap-2 rounded-lg bg-[#4253C7] px-5 text-[14px] font-medium text-white hover:bg-[#3443AD] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isParsing && <Loader2 className="h-4 w-4 animate-spin" />}
              {isParsing ? "Reading…" : "Read file"}
            </button>
          </div>
        </div>
      ) : (
        /* ── Step 2: review, then import ───────────────────────── */
        <>
          <div className="mt-6 space-y-4">
            {lists.map((list, li) => {
              const kept = list.tasks.filter((t) => t.include).length;
              const isOpen = !collapsed[li];
              return (
                <div key={`${list.name}-${li}`} className={`rounded-[14px] bg-white shadow-[0_0_4px_rgba(0,0,0,0.06)] ${list.include ? "" : "opacity-60"}`}>
                  {/* List header */}
                  <div className="flex flex-wrap items-center gap-3 border-b border-[#E8ECF5] px-5 py-4">
                    <input
                      type="checkbox"
                      checked={list.include}
                      onChange={() => toggleList(li)}
                      className="h-4 w-4 shrink-0 accent-[#4253C7]"
                      aria-label={`Include ${list.name}`}
                    />
                    <button
                      type="button"
                      onClick={() => setCollapsed((c) => ({ ...c, [li]: !c[li] }))}
                      className="shrink-0 text-[#60709C]"
                      aria-label={isOpen ? "Collapse" : "Expand"}
                    >
                      {isOpen ? <FiChevronDown size={18} /> : <FiChevronRight size={18} />}
                    </button>
                    <input
                      value={list.name}
                      onChange={(e) => editListName(li, e.target.value)}
                      className="min-w-0 flex-1 bg-transparent text-[16px] font-semibold text-[#151A2D] outline-none"
                      aria-label="List name"
                    />
                    {list.existingListId && (
                      <span className="shrink-0 rounded-full bg-[#FFF1D6] px-2.5 py-1 text-[11px] font-semibold text-[#8A6100]">
                        adds to existing list
                      </span>
                    )}
                    <span className="shrink-0 text-[12px] text-[#7D89A3]">
                      {kept} of {list.tasks.length} task{list.tasks.length === 1 ? "" : "s"}
                    </span>
                  </div>

                  {/* Tasks */}
                  {isOpen && (
                    <div className="overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                      <table className="w-full min-w-[720px]">
                        <thead>
                          <tr className="text-gray-400 text-xs border-b">
                            <th className="px-5 pb-3 pt-3 font-normal text-left w-[5%]"> </th>
                            <th className="pb-3 pt-3 font-normal text-left w-[30%]">Task</th>
                            <th className="pb-3 pt-3 font-normal text-center w-[15%]">Type</th>
                            <th className="pb-3 pt-3 font-normal text-center w-[13%]">Priority</th>
                            <th className="pb-3 pt-3 font-normal text-center w-[13%]">Status</th>
                            <th className="pb-3 pt-3 font-normal text-center w-[9%]">Hours</th>
                            <th className="pb-3 pt-3 font-normal text-center w-[15%]">Dates</th>
                          </tr>
                        </thead>
                        <tbody>
                          {list.tasks.map((task, ti) => (
                            <tr key={`${task.name}-${ti}`} className={`border-b text-sm h-[49px] last:border-b-0 ${task.include ? "" : "opacity-50"}`}>
                              <td className="px-5 py-[14px]">
                                <input
                                  type="checkbox"
                                  checked={task.include}
                                  onChange={() => toggleTask(li, ti)}
                                  className="h-4 w-4 accent-[#4253C7]"
                                  aria-label={`Include ${task.name}`}
                                />
                              </td>
                              <td className="text-xs">
                                <input
                                  value={task.name}
                                  onChange={(e) => editTask(li, ti, "name", e.target.value)}
                                  className="w-full bg-transparent text-xs text-[#172554] outline-none"
                                  aria-label="Task name"
                                />
                                {task.description && (
                                  <span className="mt-0.5 block max-w-[260px] truncate text-[11px] text-[#8A97AB]" title={task.description}>
                                    {task.description}
                                  </span>
                                )}
                              </td>
                              <td className="text-center">
                                <span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-medium capitalize ${TYPE_STYLES[task.taskType] || TYPE_STYLES.others}`}>
                                  {task.taskType}
                                </span>
                              </td>
                              <td className="text-center">
                                <span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-medium capitalize ${PRIORITY_STYLES[task.priority] || PRIORITY_STYLES.medium}`}>
                                  {task.priority}
                                </span>
                              </td>
                              <td className="text-xs text-center text-[#465675]">
                                {STATUS_LABELS[task.status] || task.status}
                              </td>
                              <td className="text-xs text-center text-[#465675]">
                                {task.estimatedHours || "—"}
                              </td>
                              <td className="text-xs text-center text-[#465675]">
                                {task.startDate || task.deadline
                                  ? `${task.startDate || "—"} → ${task.deadline || "—"}`
                                  : "—"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 mt-5 flex flex-wrap items-center justify-between gap-3 rounded-[14px] bg-white px-5 py-4 shadow-[0_-2px_8px_rgba(0,0,0,0.06)]">
            <p className="text-[13px] text-[#667085]">
              <span className="font-semibold text-[#151A2D]">{totalChosen}</span> task
              {totalChosen === 1 ? "" : "s"} across{" "}
              <span className="font-semibold text-[#151A2D]">{chosen.length}</span> list
              {chosen.length === 1 ? "" : "s"} will be created.
            </p>
            <div className="flex shrink-0 gap-3">
              <button
                type="button"
                onClick={startOver}
                className="h-10 rounded-lg border border-[#DCE2F0] px-4 text-[14px] text-[#475569] hover:bg-[#F7F8FC]"
              >
                Choose another file
              </button>
              <button
                type="button"
                onClick={doImport}
                disabled={!totalChosen || isImporting}
                className="flex h-10 min-w-[130px] items-center justify-center gap-2 rounded-lg bg-[#4253C7] px-5 text-[14px] font-medium text-white hover:bg-[#3443AD] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isImporting && <Loader2 className="h-4 w-4 animate-spin" />}
                {isImporting ? "Importing…" : "Import"}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AutoTask;
